import { useId, useState } from "react";

const SLOT_LABELS = ["DRD", "DCP", "DKJ"];

function formatWithCommas(val) {
  if (val === "" || val == null) return "";
  const str = String(val);
  const [intPart, decPart] = str.split(".");
  const formatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decPart !== undefined ? `${formatted}.${decPart}` : formatted;
}

function stripCommas(str) {
  return String(str).replace(/,/g, "");
}

function sumEntries(entries) {
  return entries.reduce((s, e) => {
    const n = parseFloat(e);
    return s + (Number.isNaN(n) ? 0 : n);
  }, 0);
}

// How many slots have saved data — scans from the last slot backwards so
// refreshing with existing data always restores the correct visible count.
function computeSavedCount(slots) {
  for (let i = slots.length - 1; i >= 1; i--) {
    if (slots[i] !== "" && !Number.isNaN(parseFloat(String(slots[i])))) {
      return i + 1;
    }
  }
  return 1;
}

export default function BreakdownFormField({
  label,
  entries = [],
  onChange,
  total = "",
  disabled = false,
  error,
  showError = true,
  required = false,
  singleInput = false,
  helperText = "",
}) {
  const baseId = useId();
  const [manualCount, setManualCount] = useState(SLOT_LABELS.length);

  const hasEntries = entries.length > 0;

  const sharedInputClass = [
    "flex-1 rounded-xl border px-3 py-2 text-sm text-palette-gray-primary outline-none transition-colors min-w-0",
    disabled
      ? "cursor-not-allowed border-palette-gray-border bg-palette-gray-subtle text-palette-gray-text"
      : "border-palette-gray-border bg-white shadow-sm hover:border-palette-gray-placeholder focus:border-palette-indigo-label focus:ring-2 focus:ring-palette-indigo-label/30",
    "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
  ].join(" ");

  // Single-input mode: classic one-field UI — no Dentrix/Tracker slots, no Add button.
  if (singleInput) {
    return (
      <div>
        <label className="block text-sm font-medium">
          {label} {required && <span className="text-smiles-red">*</span>}
        </label>
        <div className="mt-2">
          <input
            type="text"
            inputMode="decimal"
            value={formatWithCommas(total)}
            disabled={disabled}
            placeholder="0"
            onChange={(e) => {
              const raw = stripCommas(e.target.value);
              if (raw !== "" && !/^-?\d*\.?\d*$/.test(raw)) return;
              onChange([raw]);
            }}
            className={`w-full ${sharedInputClass}`}
          />
        </div>
        {helperText && <p className="mt-1 text-xs text-palette-emerald-hover">{helperText}</p>}
        {showError && error && <p className="mt-1 text-xs text-palette-rose-badge">{error}</p>}
      </div>
    );
  }

  // Disabled with no saved breakdown: show Dentrix label + read-only value
  // so all fields look consistent whether or not they have breakdown data.
  if (disabled && !hasEntries) {
    return (
      <div>
        <label className="block text-sm font-medium">
          {label} {required && <span className="text-smiles-red">*</span>}
        </label>
        <div className="mt-2 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-palette-slate-placeholder w-16 shrink-0">Dentrix</span>
            <input
              type="text"
              value={formatWithCommas(total)}
              disabled
              readOnly
              className={sharedInputClass}
            />
          </div>
        </div>
        {helperText && <p className="mt-1 text-xs text-palette-emerald-hover">{helperText}</p>}
        {showError && error && <p className="mt-1 text-xs text-palette-rose-badge">{error}</p>}
      </div>
    );
  }

  const baseEntries = hasEntries ? entries : total !== "" ? [total] : [];
  const slots = Array.from({ length: SLOT_LABELS.length }, (_, i) => baseEntries[i] ?? "");

  // savedCount is derived live from props so a page refresh with existing data
  // always shows the correct number of slots even before the user clicks Add.
  const savedCount = computeSavedCount(slots);
  const visibleCount = Math.min(SLOT_LABELS.length, Math.max(manualCount, savedCount));
  const canAddMore = !disabled && visibleCount < SLOT_LABELS.length;

  const visibleSlots = slots.slice(0, visibleCount);
  const anyFilled = visibleSlots.some((e) => e !== "" && !Number.isNaN(parseFloat(e)));
  const computedTotal = anyFilled ? sumEntries(visibleSlots) : null;

  const handleEntryChange = (idx, rawVal) => {
    const next = [...slots];
    next[idx] = rawVal;
    onChange(next);
  };

  return (
    <div>
      <label className="block text-sm font-medium">
        {label} {required && <span className="text-smiles-red">*</span>}
      </label>

      <div className="mt-2 space-y-1.5">
        {visibleSlots.map((slot, idx) => (
          <div key={idx} className="flex items-center gap-1.5">
            <span className="text-xs text-palette-slate-placeholder w-16 shrink-0">
              {SLOT_LABELS[idx]}
            </span>
            <input
              id={`${baseId}-${idx}`}
              type="text"
              inputMode="decimal"
              value={formatWithCommas(slot)}
              disabled={disabled}
              placeholder="0"
              onChange={(e) => {
                const raw = stripCommas(e.target.value);
                if (raw !== "" && !/^-?\d*\.?\d*$/.test(raw)) return;
                handleEntryChange(idx, raw);
              }}
              className={sharedInputClass}
            />
          </div>
        ))}

        {(canAddMore || computedTotal !== null) && (
          <div className="flex items-center justify-between border-t border-palette-slate-muted pt-1.5 mt-0.5">
            {canAddMore ? (
              <button
                type="button"
                onClick={() => setManualCount((c) => Math.min(SLOT_LABELS.length, c + 1))}
                className="text-xs text-palette-indigo-label hover:text-palette-indigo-heading font-medium"
              >
                + Add
              </button>
            ) : (
              <span />
            )}

            {computedTotal !== null && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-palette-slate-secondary">Total</span>
                <span className="text-xs font-semibold text-palette-slate-heading">
                  {formatWithCommas(computedTotal.toFixed(2))}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {helperText && <p className="mt-1 text-xs text-palette-emerald-hover">{helperText}</p>}
      {showError && error && <p className="mt-1 text-xs text-palette-rose-badge">{error}</p>}
    </div>
  );
}
